import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Edit, Trash2, Copy, Ban } from 'lucide-react';
import { isAmountPending } from '../../utils/bookingUtils';
import { formatShortDate, formatShortTime, formatEntryDateTime } from '../../utils/bookingFormatters';
import { SortTh } from './SortTh';

const SOURCE_COLORS = {
  'WhatsApp': 'bg-green-100 text-green-800',
  'Online': 'bg-blue-100 text-blue-800',
  'Walk-in': 'bg-amber-100 text-amber-800',
  'Calling': 'bg-purple-100 text-purple-800',
  'Khelomore': 'bg-orange-100 text-orange-800',
  'District': 'bg-cyan-100 text-cyan-800',
  'Hudle': 'bg-rose-100 text-rose-800',
  'App': 'bg-indigo-100 text-indigo-800',
};

function PendingCell({ booking }) {
  const source = booking.booking_source || '';
  const showPending = ['Walk-in', 'Calling', 'Others'].includes(source);
  if (!showPending) return <span className="text-slate-400">-</span>;
  const total = booking.amount || 0;
  const advance = booking.advance_payment || 0;
  const pending = Math.max(0, total - advance);
  if (total === 0) return <span className="text-slate-400">-</span>;
  if (pending > 0) return <span className="text-amber-600">₹{pending.toLocaleString('en-IN')}</span>;
  return <span className="text-green-600">Paid</span>;
}

function BookingRow({ booking, index, showMerged, onEdit, onCancel, onDelete }) {
  const isCancelled = booking.status === 'Cancelled' || booking.status === 'cancelled';
  return (
    <tr
      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${booking.is_duplicate ? 'bg-orange-50' : ''}`}
      data-testid={`booking-row-${booking.id}`}
    >
      <td className="p-4 text-slate-900 font-medium">
        {booking.player_name}
        {isCancelled && (
          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-100 text-rose-700" title={`Cancelled by ${booking.cancelled_by || 'admin'}`}>
            CANCELLED
          </span>
        )}
      </td>
      <td className="p-4 text-slate-600">{booking.player_mobile}</td>
      <td className="p-4 text-slate-600">{booking.venue}</td>
      <td className="p-4 text-slate-600">{booking.game}</td>
      <td className="p-4 text-slate-600">{booking.court}</td>
      <td className="p-4 text-slate-600">{formatShortDate(booking.date)}</td>
      <td className="p-4 text-slate-600 whitespace-nowrap">
        {formatShortTime(booking.start_time)} - {formatShortTime(booking.end_time)}
      </td>
      {showMerged && (
        <td className="p-4">
          {booking.merged_count > 1 ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
              {booking.merged_count} slots
            </span>
          ) : (
            <span className="text-slate-400 text-xs">1 slot</span>
          )}
        </td>
      )}
      <td className="p-4 font-semibold">
        {isAmountPending(booking.amount) ? (
          <span className="text-amber-600">Pending</span>
        ) : (
          <span className="text-slate-900">₹{booking.amount?.toLocaleString('en-IN')}</span>
        )}
      </td>
      <td className="p-4">
        {(() => {
          const src = booking.booking_source || 'N/A';
          const cls = SOURCE_COLORS[src] || 'bg-blue-100 text-blue-800';
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
              {src}
            </span>
          );
        })()}
      </td>
      <td className="p-4 font-mono text-xs text-slate-600" data-testid={`platform-id-${booking.id}`}>
        {booking.platform_booking_id || '-'}
      </td>
      <td className="p-4 text-xs text-slate-600 whitespace-nowrap" data-testid={`entry-info-${booking.id}`}>
        <div className="flex flex-col gap-0.5">
          <span>
            <span className="font-medium text-slate-800">{booking.entered_by_name || '—'}</span>
          </span>
          <span className="text-slate-400">{formatEntryDateTime(booking.created_at)}</span>
          {booking.updated_at && (
            <span className="text-amber-600">
              Edited by {booking.updated_by_name || '—'} · {formatEntryDateTime(booking.updated_at)}
            </span>
          )}
        </div>
      </td>
      <td className="p-4">
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(booking)}
            className="hover:bg-slate-100"
            data-testid={`edit-booking-${booking.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          {!isCancelled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCancel(booking)}
              className="hover:bg-amber-50 text-amber-600"
              title="Cancel booking (frees the slot)"
              data-testid={`cancel-booking-${booking.id}`}
            >
              <Ban className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(booking)}
            className="hover:bg-red-50 text-red-600"
            data-testid={`delete-booking-${booking.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </td>
      <td className="p-4 font-mono text-xs text-slate-600">
        <div className="flex items-center gap-1">
          {booking.booking_id}
          {booking.merged_count > 1 && (
            <span className="ml-1 text-blue-600" title={`Merged from ${booking.merged_count} bookings`}>
              +{booking.merged_count - 1}
            </span>
          )}
          {booking.is_duplicate && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-200 text-orange-800 ml-1" title={`Duplicate of ${booking.duplicate_of}`}>
              <Copy className="w-3 h-3 mr-0.5" />
              DUP
            </span>
          )}
        </div>
      </td>
      <td className="p-4 font-semibold"><PendingCell booking={booking} /></td>
      <td className="p-4">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
          {booking.payment_mode}
        </span>
      </td>
    </tr>
  );
}

export function BookingsTable({ bookings, showMerged, sortConfig, onSort, onEdit, onCancel, onDelete }) {
  if (bookings.length === 0) {
    return (
      <Card className="p-12 text-center border-slate-200">
        <p className="text-slate-500">No bookings found</p>
      </Card>
    );
  }

  const nonDup = bookings.filter((b) => !b.is_duplicate);
  const totalAmount = nonDup.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);

  return (
    <div className="overflow-x-auto">
      <Card className="border-slate-200">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortTh label="Player" sortKey="player_name" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Mobile" sortKey="player_mobile" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Venue" sortKey="venue" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Game" sortKey="game" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Court" sortKey="court" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Date" sortKey="date" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Time" sortKey="start_time" sortConfig={sortConfig} onSort={onSort} />
              {showMerged && <th className="text-left p-4 font-semibold text-slate-700">Slots</th>}
              <SortTh label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Source" sortKey="booking_source" sortConfig={sortConfig} onSort={onSort} />
              <th className="text-left p-4 font-semibold text-slate-700">Platform ID</th>
              <SortTh label="Entered / Updated" sortKey="created_at" sortConfig={sortConfig} onSort={onSort} />
              <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
              <SortTh label="Booking ID" sortKey="booking_id" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Pending" sortKey="pending" sortConfig={sortConfig} onSort={onSort} />
              <SortTh label="Payment" sortKey="payment_mode" sortConfig={sortConfig} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking, index) => (
              <BookingRow
                key={booking.id || index}
                booking={booking}
                index={index}
                showMerged={showMerged}
                onEdit={onEdit}
                onCancel={onCancel}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </Card>

      {/* Summary Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg mt-3" data-testid="bookings-summary">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Slots</span>
            <span className="text-lg font-bold text-white" data-testid="total-slots">{nonDup.length}</span>
          </div>
          <div className="h-6 w-px bg-zinc-700" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Total Amount</span>
            <span className="text-lg font-bold text-emerald-400" data-testid="total-amount">
              {'\u20B9'}{totalAmount.toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
